import { CSSProperties, ReactNode } from 'react';

export interface ColumnMeta {
  width: string;
  minWidth?: string;
  maxWidth?: string;
  alignHeader?: 'left' | 'right';
  hideHeader?: boolean;
  rightBorder?: string;
  headerWrapperWidth?: string;
  tdStyles?: CSSProperties;
  thStyles?: CSSProperties;
  headerWrapperStyles?: CSSProperties;
}

export interface ComponentWithChildren {
  children: ReactNode;
}

export type SelectItem = {
  id: number | string;
  value: string;
  icon?: JSX.Element;
  secondaryId?: number | string;
  tertiaryId?: number | string;
  isHighlighted?: boolean;
};

export type HierarchicalOption = {
  parent: SelectItem;
  children: SelectItem[];
};

export enum LoadingId {
  assignDealsToUser = 'assignDealsToUser',
  addDeal = 'addDeal',
  addHoliday = 'addHoliday',
  assignProxyToUser = 'assignProxyToUser',
  addUser = 'addUser',
  editUser = 'editUser',
  deleteUser = 'deleteUser',
  resetUser = 'resetUser',
  deleteDeal = 'deleteDeal',
  updateCell = 'updateCell',
  clearColumn = 'clearColumn',
  clearTable = 'clearTable',
  approveTimesheet = 'approveTimesheet',
  sendBackTimesheet = 'sendBackTimesheet',
  bulkApproveTimesheet = 'bulkApproveTimesheet',
  bulkSendBackTimesheet = 'bulkSendBackTimesheet',
  withdrawTimesheet = 'withdrawTimesheet',
  overideTimesheet = 'overideTimesheet',
}

export interface ToastProps {
  title?: string;
  message: string;
  action?: () => void;
  actionLabel?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

export enum ToastMessageType {
  info = 'info',
  warning = 'warning',
  error = 'error',
  success = 'success',
}

export interface ExtendedToastProps extends ToastProps {
  type: ToastMessageType;
}

export interface MenuItem {
  route: string;
  id: string;
  title: string;
  show: boolean;
}

export interface MainMenuItemProps extends MenuItem {
  icon: ReactNode;
  children: MenuItem[];
}

export enum THEME {
  LIGHT = 'light',
  DARK = 'dark',
}

export interface Breadcrumb {
  title: string;
  url?: string;
  color?: string;
}

export interface User {
  id: number;
  created_at?: string;
  updated_at?: string;
  name: string;
  email: string;
  /**
   * Multi-role: the full set of roles this user holds (backed by the API `user_roles` table).
   * Single source of truth — the legacy scalar `role` / `isOp` / `isAdmin` fields were dropped in
   * Phase 5 (migration M3). Use the helpers in `utils/roles.ts` (rolesOf/hasRole/firstRole/…).
   */
  roles?: UserRole[];
  isAssetServices?: boolean;
  isActive: boolean;
  operatingPartnerDeals?: UserDeal[];
  dealOwnerDeals?: UserDeal[];
  operatingPartners?: Partner[];
  operatingPartnerManagers?: Partner[];
  svpManagers?: Partner[];
  proxyOperatingPartners?: Partner[];
  proxy?: User;
  /**
   * SVP-820: whether this submitter's `proxy` may SUBMIT (and withdraw) their weekly timesheets, not
   * just fill them. Configured per (proxy, submitter) pair and reset by the API whenever the proxy
   * changes, so it only ever describes the person currently in the `proxy` slot.
   */
  proxyCanSubmit?: boolean;
  workflow?: Workflow | null;
  monthlyTimesheets?: MonthlyTimesheet[];
  endDate?: string;
  startDate?: string;
  country?: USER_COUNTRY;
  /**
   * Transient (not persisted): set only when this user appears as an EFFECTIVE deal-owner approver of a
   * timesheet row via an approval delegation — identifies the original owner whose approval was delegated
   * to them. Present on `MonthlyTimesheetRow.dealOwners` entries so the UI can flag them as "Delegated".
   */
  delegatedFromId?: number;
  delegatedFromName?: string;
}

export enum WorkflowStage {
  ACCOUNTANT = 'ACCOUNTANT',
  MANAGER = 'MANAGER',
  DEAL_OWNER = 'DEAL_OWNER',
  SVP_MANAGER = 'SVP_MANAGER',
}

export interface Workflow {
  id: number;
  key: string;
  name: string;
  isActive?: boolean;
  /** Client-defined display order (ascending; null sorts last). */
  order?: number | null;
  approvalStages?: WorkflowStage[] | null;
  defaultRows?: string[] | null;
  /**
   * Per-workflow DISPLAY-ONLY label overrides for pipeline nodes, keyed by node token
   * (SUBMITTER, ACCOUNTANT, MANAGER, DEAL_OWNER, SVP_MANAGER, CLOSED). When present, the timeline
   * renders the override instead of the default node label. Behavior never depends on this.
   */
  stageLabels?: Record<string, string> | null;
  /** Active users assigned to this workflow (computed by the API, not stored). */
  submitterCount?: number;
  /** ISO timestamp of the last change to this workflow row. */
  updated_at?: string;
}

export type UserWithTsPeriodData = { name: string; id: number } & Record<string, MonthlyTimesheet>;

export enum UserRole {
  OPERATING_PARTNER = 'Operating Partner',
  PROXY = 'Proxy',
  OP_MANAGER = 'OP Manager',
  DEAL_OWNER = 'Deal Owner',
  ACCOUNTANT = 'Accountant',
  // Multi-role refactor: first-class role replacing the legacy `isAdmin` flag.
  ADMIN = 'Admin',
}

export interface Partner {
  id: number;
  created_at: Date;
  updated_at: Date;
  name: string;
  email: string;
  /** SVP-820: set on a Proxy's `proxyOperatingPartners` — may this proxy submit for that submitter. */
  proxyCanSubmit?: boolean;
}

export interface UserDeal {
  created_at?: string;
  updated_at?: string;
  name: string;
  id: number;
}
export interface Deal {
  id: number;
  name: string;
  isActive?: boolean;
  dealOwners?: Partner[];
  operatingPartners?: Partner[];
  created_at?: string;
  updated_at?: string;
}

export interface DealWithTimeLogs extends Deal {
  dealTimeLogs: DealTimeLog[];
  totalTime: number;
}

export interface DealTimeLog {
  avg: number;
  month: number;
  year: number;
  user: User;
}

export interface Issuer {
  id: number;
  name: string;
}

export interface ActionButtonsProps {
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitDisabled?: boolean;
  cancelText?: string;
  submitText?: string;
  cancelBtnStyle?: React.CSSProperties;
  submitBtnStyle?: React.CSSProperties;
}

export interface AddUserPayload {
  name: string;
  email: string;
  // Multi-role: the authoritative set of roles (the legacy role/isOp/isAdmin columns were dropped in M3).
  roles: UserRole[];
  isAssetServices?: boolean;
  operatingPartnerManagers?: number[];
  proxy?: number;
  operatingPartnerDeals?: number[];
  proxyOperatingPartners?: number[];
  /** SVP-820: sent when editing a PROXY — the subset of `proxyOperatingPartners` they may submit for. */
  proxyCanSubmitFor?: number[];
  operatingPartners?: number[];
  dealOwnerDeals?: number[];
  svpManagers?: number[];
  workflowId?: number;
  startDate?: string;
  endDate?: string;
  country?: USER_COUNTRY;
}

export interface EditUserPayload {
  // Multi-role: the authoritative set of roles (the legacy role/isOp/isAdmin columns were dropped in M3).
  roles?: UserRole[];
  isAssetServices?: boolean;
  activate?: boolean;
  deactivate?: boolean;
  operatingPartnerManagers?: number[];
  /** `null` unassigns the proxy (Proxy Assignment page). */
  proxy?: number | null;
  /** SVP-820: sent when editing a SUBMITTER — may their proxy submit for them. */
  proxyCanSubmit?: boolean;
  operatingPartnerDeals?: number[];
  proxyOperatingPartners?: number[];
  /** SVP-820: sent when editing a PROXY — the subset of `proxyOperatingPartners` they may submit for. */
  proxyCanSubmitFor?: number[];
  operatingPartners?: number[];
  dealOwnerDeals?: number[];
  svpManagers?: number[];
  workflowId?: number;
  startDate?: string;
  endDate?: string;
  country?: USER_COUNTRY;
}

export enum TimesheetStatus {
  REJECTED = '0',
  ISSUED = '1',
  PENDING_OP_MANAGER = '2',
  PENDING_DEAL_OWNERS = '3',
  PENDING_ACCOUNTANT = '4',
  CLOSED = '5',
  PENDING_SVP_MANAGER = '6',
}

export enum TimesheetStatusLabel {
  REJECTED = 'Rejected',
  ISSUED = 'Issued',
  SUBMITTED = 'Submitted',
  PENDING = 'Pending',
  CLOSED = 'Closed',
  PENDING_OP_MANAGER = 'Pending Manager',
  PENDING_DEAL_OWNERS = 'Pending Deal Owner',
  PENDING_SVP_MANAGER = 'Pending SVP Manager',
}

export enum TimesheetLogType {
  PENDING = 'Pending',
  REJECTED = 'Rejected',
  APPROVED = 'Approved',
  SUBMITTED = 'Submitted',
  NOT_REQUIRED = 'Not Required',
}
export interface Timesheet {
  id: number;
  status: TimesheetStatus | TimesheetStatusLabel;
  created_at?: string;
  updated_at?: string;
  updatedBy?: User;
  weekStart: string;
  weekDates: string[];
  timesheetRows: TimesheetRow[];
  user?: User;
  holidays: Holiday[];
  submittedByAccountant?: boolean;
}

type MonthlyTSDataObject = {
  [key: string]: string;
};

export interface MonthlyTimesheetRow {
  id: number;
  created_at: string;
  updated_at: string;
  dealId: number;
  name: string;
  data: MonthlyTSDataObject;
  avg: number;
  dealOwners?: User[];
  isApprovedByDealOwner?: boolean;
}
export interface WeeklyTimesheet {
  id: number;
  created_at: string;
  updated_at: string;
  weekStart: string;
  weekDates: string[];
  status: TimesheetStatus;
  timesheetRows: TimesheetRow[];
  timesheetLogs: TimesheetLog[];
  user: User;
  holidays: Holiday[];
  submittedByAccountant?: boolean;
}

export interface MonthlyTimesheet {
  id: number;
  created_at?: string;
  updated_at?: string;
  month: number;
  year: number;
  status: TimesheetStatus | TimesheetStatusLabel;
  data?: MonthlyTSDataObject;
  weeklyTimesheetIds?: number[];
  weeklyTimesheets: WeeklyTimesheet[];
  avgWork: number;
  avgNotWork: number;
  rejectNote?: null | string;
  timesheetRows: MonthlyTimesheetRow[];
  user?: User;
  approvedByDealOwners?: Partner[];
  approvedByOpManagers?: User[];
  updatedBy?: User;
  timesheetLogs?: TimesheetLog[];
  /**
   * Transient, viewer-scoped flag set by the API only on the Deal Owner pending list: true when the
   * current deal owner sees this timesheet (at least partly) because approval was delegated to them.
   * Drives the "Delegated" badge/filter. Not persisted.
   */
  isDelegatedToViewer?: boolean;
}

export interface MonthlyTimesheetToExport {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  email: string;
  timesheets: MonthlyTimesheet[];
  /**
   * The submitter's workflow, used to label every exported row. A workflow is not snapshotted per
   * month, so for historical months this is the workflow they are on today.
   */
  workflow?: Workflow | null;
}

export interface AvailableMonthlyTimesheet {
  year: string;
  months: string[];
}

export interface TimesheetRow {
  id: number;
  created_at: string;
  updated_at: string;
  dealId: number;
  name: string;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  editDisabled?: boolean;
}

export interface TimesheetToOverideData {
  header: string;
  holidayName: string;
  day: number;
  tsId: number;
  weekday: WeekDay;
  data: TimesheetRowSingleDay[];
}

export interface TimesheetRowSingleDay {
  id: number;
  created_at: string;
  updated_at: string;
  dealId: number;
  name: string;
  data: number;
  editDisabled?: boolean;
}

export interface TimesheetLog {
  id: number;
  created_at: string;
  updated_at: string;
  createdBy: User;
  /**
   * Who actually performed the action when that is somebody other than `createdBy` — today a proxy
   * submitting for their submitter. Absent on self-service actions and on every log written before
   * this started being recorded.
   */
  actionedBy?: User | null;
  note: string;
  type: TimesheetLogType;
  actionStart: string;
  actionEnd: string;
  cycle: number;
  role: UserRole;
  // Approval stage this log belongs to. SVP Manager approval logs carry a null role and are
  // identified by stage === SVP_MANAGER (Phase 2).
  stage?: WorkflowStage | null;
}
export interface CreateUserPayload {
  name: string;
  email: string;
}

export interface TimesheetCell {
  id: number;
  monday: string | null;
  dealId: number | null;
  name: string | null;
  tuesday: string | null;
  wednesday: string | null;
  thursday: string | null;
  friday: string | null;
  updated_at: string;
}

export type WeekDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';

export enum AccountantTSView {
  month = 'month',
  weekday = 'weekday',
  week = 'week',
}

export interface WeekdayTimesheetTableData {
  id: number;
  dealId: number;
  name: string;
  avg: number;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
}

export interface Holiday {
  id: number;
  country: string;
  isMultiday: boolean;
  name: string;
  type: string;
  startDate: string;
  created_at?: string;
  day: number;
  endDate: string | null;
  month: number;
  multidayId: string;
  updated_at: string;
  year: number;
}

export interface HolidayExcelRow {
  name: string;
  country: string;
  type: string;
  startDate: Date;
  endDate: Date | null;
}

export interface OpManagerSummaryRowData {
  '1'?: number | null;
  '2'?: number | null;
  '3'?: number | null;
  '4'?: number | null;
  '5'?: number | null;
  '6'?: number | null;
  '7'?: number | null;
  '8'?: number | null;
  '9'?: number | null;
  '10'?: number | null;
  '11'?: number | null;
  '12'?: number | null;
  dealId?: number;
  dealName?: string;
  ops?: OpManagerSummaryRowData[];
  userId?: number;
  userName?: string;
}

export enum USER_COUNTRY {
  US = 'US',
  UK = 'UK',
}

export enum HOLIDAY_TYPE {
  BANK_HOLIDAY = 'Bank Holiday',
  DUVET_DAY = 'Duvet Day',
  OFFSITE_EVENT = 'Offsite Event',
  COMPANY_EVENT = 'Company Event',
  GENERAL_RESTRUCTURING = 'General Restructuring',
  OTHER = 'Other',
}

export const HolidayTypeItems: SelectItem[] = [
  {
    id: 1,
    value: HOLIDAY_TYPE.BANK_HOLIDAY,
  },
  {
    id: 2,
    value: HOLIDAY_TYPE.DUVET_DAY,
  },
  {
    id: 3,
    value: HOLIDAY_TYPE.OFFSITE_EVENT,
  },
  {
    id: 4,
    value: HOLIDAY_TYPE.COMPANY_EVENT,
  },
  {
    id: 5,
    value: HOLIDAY_TYPE.GENERAL_RESTRUCTURING,
  },
  {
    id: 6,
    value: HOLIDAY_TYPE.OTHER,
  },
];

export enum HolidayTab {
  Calendar,
  List,
}

export enum EXPORT_MODE {
  TIMESHEETS = 'Timesheets',
  ACTIVITY_LOGS = 'Activity Logs',
  // Unlike the two above, this one takes no selection and no period - it downloads straight away.
  OPEN_TIMESHEETS = 'Export Open Timesheets',
}

export enum TS_RANGE_OPTIONS {
  LAST_6_MONTHS = 'Last 6 months',
  LAST_9_MONTHS = 'Last 9 months',
  LAST_12_MONTHS = 'Last 12 months',
  CUSTOM_RANGE = 'Custom range',
}

export const TsRangeTypeItems: SelectItem[] = [
  {
    id: 6,
    value: TS_RANGE_OPTIONS.LAST_6_MONTHS,
  },
  {
    id: 9,
    value: TS_RANGE_OPTIONS.LAST_9_MONTHS,
  },
  {
    id: 12,
    value: TS_RANGE_OPTIONS.LAST_12_MONTHS,
  },
  {
    id: 1,
    value: TS_RANGE_OPTIONS.CUSTOM_RANGE,
  }
];